// Error reporting for the two browser consoles.
//
// The consumer app already reports to the backend's mobile_telemetry endpoint,
// but its provider is React Native only - AsyncStorage, expo-constants,
// expo-updates, AppState. Admin and stylist had no error reporting at all, and
// production bore that out: of the three apps the endpoint accepts, only
// stylist had ever produced a single event, and admin none.
//
// No backend change was needed. `MobileApp` already admits 'admin' and
// 'stylist' and `MobilePlatform` already admits 'web'.
//
// The backend schema is a strict allowlist (`extra="forbid"`): a fixed signal
// enum, a three-value failure code, and no free-form message or attribute
// field anywhere. So this cannot leak a stack trace or a member's data even by
// accident - there is nowhere to put one. What it answers is "is this app
// throwing, and on which screen", not "what was the exception".

export type WebApp = 'admin' | 'stylist';

type Signal =
  | 'app_started'
  | 'app_foregrounded'
  | 'app_backgrounded'
  | 'crash'
  | 'network_failure'
  | 'slow_render'
  | 'hang'
  | 'startup_stall'
  | 'navigation';

type FailureCode = 'network_unavailable' | 'request_timeout' | 'non_json_response';

type DurationBucket = 'under_250ms' | 'under_1s' | 'under_3s' | 'over_3s' | 'not_applicable';

type BatchEvent = {
  app: WebApp;
  platform: 'web';
  event: Signal;
  release: string;
  app_version: string;
  schema_version: 1;
  sequence: number;
  monotonic_ms: number;
  screen?: string;
  component?: string;
  failure_code?: FailureCode;
  duration_bucket?: DurationBucket;
};

export type WebDiagnosticsOptions = {
  app: WebApp;
  /** Origin of the API, e.g. https://api.closettheory.co */
  apiBaseUrl: string;
  /** Built commit, so an event can be tied to what was serving. */
  release: string;
  appVersion: string;
};

export type WebDiagnostics = {
  reportCrash(where?: { screen?: string; component?: string }): void;
  reportNetworkFailure(failureCode: FailureCode, where?: { screen?: string }): void;
  reportNavigation(screen: string): void;
  /** Flushes anything queued. Exposed for tests and for sign-out. */
  flush(): Promise<void>;
  stop(): void;
};

const BATCH_PATH = '/api/v1/telemetry/mobile/batches';
// The backend's field caps (mobile_telemetry/schemas.py). Braces are outside
// its screen grammar, so the placeholder is plain.
const SCREEN_MAX_CHARS = 80;
const RELEASE_MAX_CHARS = 32;
const ID_PLACEHOLDER = '_id_';
const INSTALL_ID_KEY = 'ct-web-telemetry-install-id';
// Mirrors the backend's own caps in mobile_telemetry/router.py.
const MAX_BATCH_EVENTS = 50;
// Bounds growth while offline. Oldest go first: a stale crash from ten minutes
// ago is worth less than not growing without limit.
const MAX_QUEUE_EVENTS = 200;
const FLUSH_DEBOUNCE_MS = 2_000;
// A burst of identical errors - a render loop throwing every frame - must not
// become a self-inflicted request flood.
const MAX_EVENTS_PER_WINDOW = 60;
const RATE_WINDOW_MS = 60_000;

function uuid(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  // randomUUID needs a secure context; a local http origin has getRandomValues
  // but not randomUUID.
  const bytes = new Uint8Array(16);
  cryptoApi?.getRandomValues?.(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Reduces a path to its route shape. `/consumers/9f2c.../outfits` becomes
 * `/consumers/_id_/outfits`, so a screen name can never carry a member id.
 */
export function routeShape(path: string): string {
  const withoutQuery = path.split(/[?#]/, 1)[0] ?? path;
  return (
    withoutQuery
      .split('/')
      .map((segment) => {
        if (!segment) return segment;
        if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment)) return ID_PLACEHOLDER;
        if (/^\d+$/.test(segment)) return ID_PLACEHOLDER;
        if (/^[A-Za-z0-9_-]{16,}$/.test(segment) && /\d/.test(segment)) return ID_PLACEHOLDER;
        return safeToken(segment, SCREEN_MAX_CHARS);
      })
      .join('/')
      .slice(0, SCREEN_MAX_CHARS) || '/'
  );
}

/**
 * Coerces a value into the backend's token grammar (`[A-Za-z0-9._/-]` for
 * screen and component, `[A-Za-z0-9._-]` for release). The schema forbids
 * everything else, and one bad field 422s the whole batch.
 */
function safeToken(value: string, maxChars: number, allowSlash = true): string {
  const disallowed = allowSlash ? /[^A-Za-z0-9._/-]/g : /[^A-Za-z0-9._-]/g;
  return value.replace(disallowed, '_').slice(0, maxChars);
}

/**
 * The bundle chunk a runtime error came from, as its file name only. The full
 * `https://…/assets/index-abc.js` carries `:` and runs past the schema's cap.
 */
function chunkName(filename: string): string | undefined {
  const withoutQuery = filename.split(/[?#]/, 1)[0] ?? filename;
  const base = withoutQuery.split('/').filter(Boolean).pop();
  return base ? safeToken(base, SCREEN_MAX_CHARS, false) : undefined;
}

/**
 * The deploy workflows pass the full 40-character commit SHA; the backend
 * caps release at 32. A short SHA is what every other surface shows anyway.
 */
export function releaseToken(release: string): string {
  const trimmed = /^[0-9a-f]{40}$/i.test(release) ? release.slice(0, 12) : release;
  return safeToken(trimmed, RELEASE_MAX_CHARS, false) || 'unknown';
}

function readInstallId(): string {
  // Private windows and blocked site data throw on access rather than
  // returning null, so every read and write is guarded.
  try {
    const existing = globalThis.localStorage?.getItem(INSTALL_ID_KEY);
    if (existing) return existing;
    const created = uuid();
    globalThis.localStorage?.setItem(INSTALL_ID_KEY, created);
    return created;
  } catch {
    return uuid();
  }
}

export function startWebDiagnostics(options: WebDiagnosticsOptions): WebDiagnostics {
  const { app, apiBaseUrl, appVersion } = options;
  const release = releaseToken(options.release);
  const sessionId = uuid();
  const installId = readInstallId();
  const startedAt = Date.now();

  let queue: BatchEvent[] = [];
  let sequence = 0;
  let flushing = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let windowStartedAt = Date.now();
  let eventsInWindow = 0;
  let stopped = false;

  function allowed(): boolean {
    const now = Date.now();
    if (now - windowStartedAt >= RATE_WINDOW_MS) {
      windowStartedAt = now;
      eventsInWindow = 0;
    }
    if (eventsInWindow >= MAX_EVENTS_PER_WINDOW) return false;
    eventsInWindow += 1;
    return true;
  }

  function currentScreen(): string | undefined {
    try {
      return routeShape(globalThis.location?.pathname ?? '');
    } catch {
      return undefined;
    }
  }

  function enqueue(
    event: Signal,
    where?: { screen?: string; component?: string; failureCode?: FailureCode },
  ): void {
    if (stopped || !allowed()) return;
    sequence += 1;
    const record: BatchEvent = {
      app,
      platform: 'web',
      event,
      release,
      app_version: appVersion,
      schema_version: 1,
      sequence,
      monotonic_ms: Math.round(Date.now() - startedAt),
    };
    const screen = where?.screen ? routeShape(where.screen) : currentScreen();
    if (screen) record.screen = screen;
    if (where?.component) record.component = safeToken(where.component, SCREEN_MAX_CHARS);
    if (where?.failureCode) record.failure_code = where.failureCode;
    queue.push(record);
    if (queue.length > MAX_QUEUE_EVENTS) queue.splice(0, queue.length - MAX_QUEUE_EVENTS);
    schedule();
  }

  function schedule(): void {
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      void flush();
    }, FLUSH_DEBOUNCE_MS);
  }

  function body(events: BatchEvent[]): string {
    return JSON.stringify({
      schema_version: 1,
      batch_id: uuid(),
      install_id: installId,
      session_id: sessionId,
      events,
    });
  }

  async function flush(): Promise<void> {
    if (flushing || queue.length === 0) return;
    flushing = true;
    const batch = queue.splice(0, MAX_BATCH_EVENTS);
    try {
      const response = await fetch(new URL(BATCH_PATH, apiBaseUrl).toString(), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: body(batch),
        // The endpoint is unauthenticated and identifies the client by
        // install_id, so sending cookies would only widen what it receives.
        credentials: 'omit',
        keepalive: true,
      });
      // A 4xx means this payload is invalid and would fail identically
      // forever; only retry what a retry could fix.
      if (!response.ok && (response.status >= 500 || response.status === 429)) {
        queue = [...batch, ...queue].slice(0, MAX_QUEUE_EVENTS);
      }
    } catch {
      // Offline, or the API is unreachable. Never report this as a
      // network_failure event - that is how a reporting outage turns into a
      // request flood against the endpoint that is already failing.
      queue = [...batch, ...queue].slice(0, MAX_QUEUE_EVENTS);
    } finally {
      flushing = false;
    }
  }

  function flushOnHide(): void {
    if (queue.length === 0) return;
    const batch = queue.splice(0, MAX_BATCH_EVENTS);
    // A page being unloaded cannot await a fetch, but a keepalive fetch
    // outlives it. Not sendBeacon: a beacon always goes with credentials
    // mode 'include', so a cross-origin JSON beacon is preflighted as a
    // credentialed request, and the API (rightly) never answers
    // Access-Control-Allow-Credentials: true. Every beacon failed CORS.
    try {
      void fetch(new URL(BATCH_PATH, apiBaseUrl).toString(), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: body(batch),
        credentials: 'omit',
        keepalive: true,
      }).catch(() => undefined);
      return;
    } catch {
      // Falls through to re-queueing.
    }
    queue = [...batch, ...queue].slice(0, MAX_QUEUE_EVENTS);
  }

  const onError = (event: ErrorEvent): void => {
    // The filename is the bundle chunk, not a member-facing path, but it goes
    // through routeShape anyway rather than trusting that.
    const component = event.filename ? chunkName(event.filename) : undefined;
    enqueue('crash', component ? { component } : undefined);
  };
  const onRejection = (): void => {
    enqueue('crash');
  };
  const onVisibility = (): void => {
    if (globalThis.document?.visibilityState === 'hidden') {
      enqueue('app_backgrounded');
      flushOnHide();
    } else {
      enqueue('app_foregrounded');
    }
  };

  globalThis.addEventListener?.('error', onError);
  globalThis.addEventListener?.('unhandledrejection', onRejection);
  globalThis.document?.addEventListener?.('visibilitychange', onVisibility);
  globalThis.addEventListener?.('pagehide', flushOnHide);

  enqueue('app_started');

  return {
    reportCrash: (where) => enqueue('crash', where),
    reportNetworkFailure: (failureCode, where) =>
      enqueue(
        'network_failure',
        where?.screen ? { screen: where.screen, failureCode } : { failureCode },
      ),
    reportNavigation: (screen) => enqueue('navigation', { screen }),
    flush,
    stop: () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      globalThis.removeEventListener?.('error', onError);
      globalThis.removeEventListener?.('unhandledrejection', onRejection);
      globalThis.document?.removeEventListener?.('visibilitychange', onVisibility);
      globalThis.removeEventListener?.('pagehide', flushOnHide);
    },
  };
}
